/** @jsx isolateComponent */
import { css } from '@emotion/core';
import {
  Animation,
  Colors,
  Heading,
  HeadingLevel,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../universal-nav/hooks/useUniversalNavContext';
import type { UniversalNavLinkType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../ArrowLinkIcon';
import { HEADING_GAP, ICON_SIZE } from './constants';

type Props = {
  /**
   * A Top level heading inside the MegaMenu
   * Can be a Link or a span
   */
  link: UniversalNavLinkType;
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('SubMenuHeading', {
  root: css({
    textDecoration: 'none',
    color: Colors.inherit,
    outline: 'none',
    '&:hover > p, &:focus > p': {
      textDecoration: 'underline',
    },
    '&:hover > p > span, &:focus > p > span': {
      marginLeft: 4,
      opacity: 1,
    },
  }),
  headingWrapper: css({
    display: 'flex',
    gap: HEADING_GAP,
    alignItems: 'end',
    marginBottom: 10,
  }),
  headingText: css({
    ...Typography.subHeader5Bold,
  }),
  description: (hasIcon: boolean) =>
    css({
      paddingLeft: hasIcon ? `${ICON_SIZE + HEADING_GAP}px` : 0,
      ...Typography.paragraph3,
      color: Colors.neutral700,
      maxWidth: 202,
      position: 'relative',
      transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    }),
  icon: css({
    color: Colors.inherit,
    fontSize: 20,
    lineHeight: '20px',
  }),
});

/**
 * @private
 * Group heading inside the MegaMenu
 */
export const SubMenuHeading = (props: Props) => {
  const { link } = props;
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string, isLink: boolean) => {
    if (isLink) {
      emitUserInteractionEvent({
        event_action: eventType,
        option_selected: `MegaMenuSubmenuLink: ${text}`,
      });
      if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
    }
  };

  const LinkComponent = link.href ? Link : 'span';

  return (
    <HeadingLevel>
      <Heading>
        <LinkComponent
          css={Styles.root}
          // @ts-ignore it doesn't matter if `color` is `undefined` on a `span`.
          color={null}
          href={link.href ? getFullyQualifiedURL(link.href) : undefined}
          onClick={() => handleEvent(link.text, 'click', !!link.href)}
          onMouseEnter={() => handleEvent(link.text, 'hover', !!link.href)}
        >
          <div css={Styles.headingWrapper}>
            {link.icon && (
              <span css={Styles.icon} data-testid="heading-icon">
                <Icon name={link.icon} size={ICON_SIZE} />
              </span>
            )}
            <span css={Styles.headingText}>{link.text}</span>
          </div>
          {link.description && (
            <p
              css={Styles.description(!!link.icon)}
              data-testid="heading-description"
            >
              {link.description}
              <ArrowLinkIcon />
            </p>
          )}
        </LinkComponent>
      </Heading>
    </HeadingLevel>
  );
};
